import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Download, FileText, CurrencyDollar, Warning, CheckCircle } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toDDMMYYYY, todayISO } from '../lib/dateUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { loadCompanies, getCompaniesCache } from '../lib/companies';
import { Amount, useAmountVisibility } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  paid: { bg: '#dcfce7', color: '#166534' },
  unpaid: { bg: '#fee2e2', color: '#991b1b' },
  partial: { bg: '#fef9c3', color: '#854d0e' },
  overdue: { bg: '#fce7f3', color: '#9d174d' },
  draft: { bg: '#f3f4f6', color: '#374151' },
};

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const firstOfYearISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const monthLabel = (ym) => {
  try {
    const [y, m] = ym.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
  } catch { return ym; }
};

export const InvoiceReportsPage = () => {
  const { hidden: amountsHidden } = useAmountVisibility();
  const [fromDate, setFromDate] = useState(firstOfYearISO());
  const [toDate, setToDate] = useState(todayISO());
  const [companyId, setCompanyId] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCompanies().then(setCompanies);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (companyId !== 'all') params.company_id = companyId;
      const r = await axios.get(`${API_URL}/api/reports/invoices`, {
        params, withCredentials: true,
      });
      setReport(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [fromDate, toDate, companyId]);

  const exportCSV = async () => {
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (companyId !== 'all') params.company_id = companyId;
      const res = await axios.get(`${API_URL}/api/reports/invoices.csv`, {
        params, withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${companyId}_${fromDate}_${toDate}.csv`;
      a.click();
    } catch (e) { alert('Export failed: ' + e.message); }
  };

  const chartData = useMemo(() => (report?.monthly || []).map((m) => ({
    month: monthLabel(m.month), Invoiced: m.invoiced, Paid: m.paid, Due: m.due,
  })), [report]);

  const summaryCards = report ? [
    { title: 'Total Invoiced', raw: report.summary.total_invoiced, sub: `${report.summary.total_invoices} invoices`, icon: FileText, color: 'var(--dx-primary)' },
    { title: 'Total Paid',     raw: report.summary.total_paid,     sub: 'Received',     icon: CheckCircle, color: '#0F6E56' },
    { title: 'Total Due',      raw: report.summary.total_due,      sub: 'Outstanding',  icon: Warning, color: '#A32D2D' },
    { title: 'Total Tax',      raw: report.summary.total_tax,      sub: 'Collected',    icon: CurrencyDollar, color: '#185FA5' },
  ] : [];

  const statusEntries = report?.status_breakdown ? Object.entries(report.status_breakdown) : [];

  return (
    <div data-testid="invoice-reports-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Invoice Reports</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Revenue, dues, top clients & monthly trends</p>
        </div>
        <Button onClick={exportCSV} variant="outline" data-testid="export-invoices-csv">
          <Download size={16} className="mr-1" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="dx-card p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Company</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="company-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map((co) => (<SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="from-date" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="to-date" />
          </div>
          <div className="text-xs" style={{ color: 'var(--dx-text2)' }}>
            Showing: {toDDMMYYYY(fromDate)} → {toDDMMYYYY(toDate)}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm py-12 text-center" style={{ color: 'var(--dx-text2)' }}>Loading…</div>
      ) : !report ? null : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {summaryCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="dx-stat" data-testid={`inv-rep-card-${i}`}>
                  <div className="dx-stat-label">
                    <Icon size={13} />
                    <span>{c.title}</span>
                  </div>
                  <div className="dx-stat-value" style={{ color: c.color, fontSize: '22px' }}>
                    <Amount value={c.raw} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} />
                  </div>
                  <div className="dx-stat-sub">{c.sub}</div>
                </div>
              );
            })}
          </div>

          {/* Monthly trend chart */}
          <Card className="dx-card p-5 mb-6">
            <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--dx-text)' }}>Monthly Trend</h3>
            {amountsHidden ? (
              <div className="h-[300px] flex flex-col items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400">
                <div className="text-3xl">🔒</div>
                <div className="text-sm mt-1.5 font-medium">Monthly trend hidden</div>
                <div className="text-[11px] mt-0.5">Click the eye icon in the header to show</div>
              </div>
            ) : chartData.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--dx-text3)' }}>No invoices in the selected range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="Invoiced" fill="#1D9E75" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Paid" fill="#185FA5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Due" fill="#A32D2D" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Status breakdown */}
            <Card className="dx-card">
              <div className="p-4" style={{ borderBottom: '1px solid var(--dx-border)' }}>
                <h3 className="text-base font-semibold" style={{ color: 'var(--dx-text)' }}>Status Breakdown</h3>
              </div>
              <Table>
                <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
                  <TableHead>STATUS</TableHead>
                  <TableHead className="text-right">COUNT</TableHead>
                  <TableHead className="text-right">AMOUNT</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {statusEntries.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-6" style={{ color: 'var(--dx-text3)' }}>No data</TableCell></TableRow>
                  ) : statusEntries.map(([status, val]) => {
                    const col = STATUS_COLOR[status] || STATUS_COLOR.unpaid;
                    return (
                      <TableRow key={status} data-testid={`status-row-${status}`}>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase"
                            style={{ background: col.bg, color: col.color }}>{status}</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{val.count}</TableCell>
                        <TableCell className="text-right font-semibold"><Amount value={val.amount} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>

            {/* Top clients */}
            <Card className="dx-card">
              <div className="p-4" style={{ borderBottom: '1px solid var(--dx-border)' }}>
                <h3 className="text-base font-semibold" style={{ color: 'var(--dx-text)' }}>Top 10 Clients</h3>
              </div>
              <Table>
                <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
                  <TableHead>CLIENT</TableHead>
                  <TableHead className="text-right">INVOICED</TableHead>
                  <TableHead className="text-right">DUE</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(report?.top_clients || []).length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-6" style={{ color: 'var(--dx-text3)' }}>No data</TableCell></TableRow>
                  ) : (report.top_clients || []).map((c) => (
                    <TableRow key={c.client_id} data-testid={`top-client-${c.client_id}`}>
                      <TableCell>
                        <div className="font-medium">{c.client_name}</div>
                        {c.company_name && <div className="text-xs" style={{ color: 'var(--dx-text3)' }}>{c.company_name}</div>}
                      </TableCell>
                      <TableCell className="text-right font-semibold"><Amount value={c.invoiced} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                      <TableCell className="text-right" style={{ color: c.due > 0 ? '#A32D2D' : 'var(--dx-text3)' }}><Amount value={c.due} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Monthly table */}
          <Card className="dx-card mt-5">
            <div className="p-4" style={{ borderBottom: '1px solid var(--dx-border)' }}>
              <h3 className="text-base font-semibold" style={{ color: 'var(--dx-text)' }}>Monthly Summary</h3>
            </div>
            <Table>
              <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
                <TableHead>MONTH</TableHead>
                <TableHead className="text-right">INVOICES</TableHead>
                <TableHead className="text-right">INVOICED</TableHead>
                <TableHead className="text-right">PAID</TableHead>
                <TableHead className="text-right">DUE</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(report?.monthly || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-6" style={{ color: 'var(--dx-text3)' }}>No data</TableCell></TableRow>
                ) : (report.monthly || []).map((m) => (
                  <TableRow key={m.month} data-testid={`month-row-${m.month}`}>
                    <TableCell className="font-medium">{monthLabel(m.month)}</TableCell>
                    <TableCell className="text-right">{m.count}</TableCell>
                    <TableCell className="text-right"><Amount value={m.invoiced} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                    <TableCell className="text-right" style={{ color: '#0F6E56' }}><Amount value={m.paid} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                    <TableCell className="text-right" style={{ color: m.due > 0 ? '#A32D2D' : 'var(--dx-text3)' }}><Amount value={m.due} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
};

export default InvoiceReportsPage;
